import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { videoBankAPI } from '../lib/api';
import { chunkedUpload } from '../lib/chunkedUpload';
import { toast } from 'sonner';
import {
  Video, Film, Upload, UploadCloud, Loader2, Search, Download,
  Copy, Trash2, Play, Store, ExternalLink,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const formatSize = (bytes) => {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(1)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

export default function VideoBank() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('all');
  const [uploads, setUploads] = useState([]); // { name, progress, error }
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [active, setActive] = useState(null); // lightbox item
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await videoBankAPI.list({ source });
      setVideos(res.data || []);
    } catch {
      toast.error('Failed to load video bank');
    } finally {
      setLoading(false);
    }
  }, [source]);

  useEffect(() => { load(); }, [load]);

  const doUpload = async (fileList) => {
    const all = Array.from(fileList || []);
    const files = all.filter((f) => f.type.startsWith('video/'));
    const rejected = all.length - files.length;
    if (rejected > 0) toast.error(`${rejected} non-video file${rejected > 1 ? 's' : ''} skipped`);
    if (!files.length) return;
    setUploading(true);
    setUploads(files.map((f) => ({ name: f.name, progress: 0, error: null })));
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      try {
        await chunkedUpload(files[i], 'video_bank', 'bank', (p) => {
          setUploads((prev) => prev.map((u, idx) => (idx === i ? { ...u, progress: p } : u)));
        });
        ok += 1;
      } catch (err) {
        setUploads((prev) => prev.map((u, idx) => (idx === i ? { ...u, error: err.response?.data?.detail || 'Upload failed' } : u)));
      }
    }
    setUploading(false);
    if (ok > 0) toast.success(`${ok} video${ok > 1 ? 's' : ''} added to the bank`);
    if (fileRef.current) fileRef.current.value = '';
    setTimeout(() => setUploads([]), 2500);
    await load();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    doUpload(e.dataTransfer.files);
  };

  const copyLink = async (item) => {
    if (!item.view_url) { toast.error('No shareable link available'); return; }
    try {
      await navigator.clipboard.writeText(item.view_url);
      toast.success('Share link copied (valid 7 days)');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleDelete = async (item) => {
    try {
      setDeleting(true);
      await videoBankAPI.remove(item.id);
      toast.success('Video removed');
      setActive(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const filtered = search
    ? videos.filter((v) => (v.name || '').toLowerCase().includes(search.toLowerCase()) ||
                            (v.source_label || '').toLowerCase().includes(search.toLowerCase()))
    : videos;

  return (
    <div className="space-y-6" data-testid="video-bank-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-indigo-50 via-white to-violet-50/40 border border-indigo-100 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-indigo-600 rounded-lg">
            <Film size={16} className="text-white" />
          </div>
          <span className="text-xs uppercase tracking-widest text-indigo-700 font-medium">Video Bank</span>
        </div>
        <h1 className="text-3xl font-semibold text-zinc-900 tracking-tight">Video Library</h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          One home for every product &amp; content video. Upload straight here (multiple files, original quality — no compression),
          and every video uploaded to a listing shows up automatically. View, download, or copy a shareable link in one click.
        </p>
      </div>

      {/* Upload zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`rounded-xl border-2 border-dashed transition-colors p-6 ${dragOver ? 'border-indigo-400 bg-indigo-50/60' : 'border-zinc-200 bg-white'}`}
        data-testid="video-bank-upload-zone"
      >
        <input
          ref={fileRef}
          type="file"
          accept="video/*"
          multiple
          className="hidden"
          onChange={(e) => doUpload(e.target.files)}
          data-testid="video-bank-upload-input"
        />
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-100 rounded-full"><UploadCloud size={20} className="text-indigo-600" /></div>
            <div>
              <p className="text-sm font-semibold text-zinc-900">Drag &amp; drop videos here</p>
              <p className="text-xs text-zinc-500">MP4, MOV, WEBM — stored at full original quality</p>
            </div>
          </div>
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="video-bank-pick-btn"
          >
            {uploading
              ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Uploading…</>
              : <><Upload size={15} className="mr-1.5" /> Select videos</>}
          </Button>
        </div>

        {uploads.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="video-bank-upload-progress">
            {uploads.map((u, i) => (
              <div key={i} className="bg-zinc-50 border border-zinc-100 rounded-md px-3 py-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="truncate text-zinc-700 max-w-[70%]">{u.name}</span>
                  <span className={u.error ? 'text-red-600' : 'text-zinc-500'}>{u.error ? u.error : `${u.progress}%`}</span>
                </div>
                {!u.error && (
                  <div className="mt-1 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-500 transition-all" style={{ width: `${u.progress}%` }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Search by filename or listing…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10 text-sm bg-white border-zinc-200"
            data-testid="video-bank-search"
          />
        </div>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-full sm:w-[200px] h-10 bg-white border-zinc-200 text-sm" data-testid="video-bank-source-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="bank">Uploaded to Bank</SelectItem>
            <SelectItem value="listing">From Listings</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-16 text-center" data-testid="video-bank-empty">
          <Video size={36} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-sm text-zinc-500 font-medium">{search ? 'No videos match your search' : 'No videos yet'}</p>
          {!search && <p className="text-xs text-zinc-400 mt-1">Drop a video above or upload one to a listing.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="video-bank-grid">
          {filtered.map((v) => (
            <div
              key={v.key}
              onClick={() => setActive(v)}
              className="group bg-white border border-zinc-200 rounded-xl overflow-hidden flex flex-col hover:border-indigo-300 hover:shadow-sm transition-all cursor-pointer"
              data-testid={`video-bank-card-${v.key}`}
            >
              <div className="relative aspect-video bg-zinc-900 overflow-hidden">
                {v.view_url ? (
                  <video
                    src={`${v.view_url}#t=0.1`}
                    preload="metadata"
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-zinc-600"><Video size={36} /></div>
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/25 transition-colors">
                  <div className="w-11 h-11 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm">
                    <Play size={18} className="text-zinc-900 ml-0.5" fill="currentColor" />
                  </div>
                </div>
                <Badge
                  className={`absolute top-2.5 left-2.5 text-[10px] uppercase tracking-wider border-0 inline-flex items-center gap-1 ${v.source_type === 'listing' ? 'bg-rose-600 text-white' : 'bg-indigo-600 text-white'}`}
                >
                  {v.source_type === 'listing' ? <Store size={10} /> : <Film size={10} />}
                  {v.source_type === 'listing' ? 'Listing' : 'Bank'}
                </Badge>
              </div>
              <div className="p-3 flex flex-col gap-1.5 flex-1">
                <p className="text-sm font-medium text-zinc-900 line-clamp-1" title={v.name}>{v.name}</p>
                <p className="text-xs text-zinc-500 line-clamp-1">{v.source_label}</p>
                <div className="flex items-center justify-between text-[11px] text-zinc-400 mt-auto pt-1.5">
                  <span>{formatSize(v.file_size)}</span>
                  <span>{formatDate(v.uploaded_at)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={!!active} onOpenChange={(o) => { if (!o) setActive(null); }}>
        <DialogContent className="sm:max-w-3xl bg-white text-zinc-900 p-0 overflow-hidden" data-testid="video-lightbox">
          {active && (
            <div>
              <DialogTitle className="sr-only">{active.name}</DialogTitle>
              <DialogDescription className="sr-only">Video player with download and share options</DialogDescription>
              <div className="bg-black aspect-video">
                <video src={active.view_url} controls autoPlay playsInline className="w-full h-full" data-testid="video-lightbox-player" />
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <h3 className="text-base font-semibold text-zinc-900 break-words">{active.name}</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {active.source_label} · {formatSize(active.file_size)} · {formatDate(active.uploaded_at)}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a href={active.download_url} target="_blank" rel="noreferrer">
                    <Button className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="video-download-btn">
                      <Download size={14} className="mr-1.5" /> Download
                    </Button>
                  </a>
                  <Button variant="outline" className="border-zinc-200 text-zinc-700" onClick={() => copyLink(active)} data-testid="video-share-btn">
                    <Copy size={14} className="mr-1.5" /> Copy share link
                  </Button>
                  {active.source_type === 'listing' && active.source_id && (
                    <Button
                      variant="outline"
                      className="border-zinc-200 text-zinc-700"
                      onClick={() => navigate(`/etsy/${active.source_id}`)}
                      data-testid="video-open-listing-btn"
                    >
                      <ExternalLink size={14} className="mr-1.5" /> Open listing
                    </Button>
                  )}
                  {active.deletable && (
                    <Button
                      variant="outline"
                      disabled={deleting}
                      className="border-red-200 text-red-600 hover:bg-red-50 ml-auto"
                      onClick={() => handleDelete(active)}
                      data-testid="video-delete-btn"
                    >
                      {deleting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Trash2 size={14} className="mr-1.5" />} Delete
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
